import { useState, useEffect } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { useToast } from '../components/ui/use-toast';
import { UserPlus, X, Search } from 'lucide-react';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { competitionsService } from '../services/competitions';
import { staffService } from '../services/staff';
import { ZoneManager } from '../components/gym/ZoneManager';
import { useAuth } from '../hooks/useAuth';
import type { Competition } from '../types/competition';
import type { Staff, User } from '../services/staff';

export default function CompetitionSettings() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();

  const [competition, setCompetition] = useState<Competition | null>(null);
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState<Staff[]>([]);

  // Get current user staff info for this competition from user profile
  const currentUserStaff = user?.staff?.find(staff => staff.gym_slug === slug) || null;

  // Info form state
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [isSavingInfo, setIsSavingInfo] = useState(false);

  // Add staff form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (!slug) return;

    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const competitionData = await competitionsService.getCompetitionBySlug(slug);

        if (isMounted) {
          setCompetition(competitionData);

          // Initialize info form from competition data
          setWebsiteUrl(competitionData.website_url || '');
          setAddress(competitionData.address || '');
          setCity(competitionData.city || '');
          setZipcode(competitionData.zipcode || '');
          setLatitude(competitionData.latitude != null ? String(competitionData.latitude) : '');
          setLongitude(competitionData.longitude != null ? String(competitionData.longitude) : '');

          // Check if user is staff admin based on auth context
          if (currentUserStaff?.staff_roles.includes('admin') && currentUserStaff?.status === 'confirmed') {
            const staff = await staffService.getLocationStaff(competitionData.id);
            setStaffList(staff);
          } else {
            // User is not admin, redirect to competition profile
            navigate(`/competitions/${slug}`, { replace: true });
            return;
          }
        }
      } catch (error) {
        console.error('Failed to fetch competition data:', error);
        if (isMounted) {
          toast({
            title: t('competitionSettings.failedToLoad'),
            description: error instanceof Error ? error.message : t('errors.unexpectedError'),
            variant: 'destructive',
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [slug, navigate, t, toast, currentUserStaff?.staff_roles, currentUserStaff?.status]);

  // Search users
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      try {
        const results = await staffService.searchUsers(searchQuery);
        setSearchResults(results);
      } catch (error) {
        console.error('Search failed:', error);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [searchQuery]);

  const handleSaveInfo = async () => {
    if (!competition || !slug) return;

    setIsSavingInfo(true);
    try {
      const updatedCompetition = await competitionsService.updateCompetition(slug, {
        website_url: websiteUrl || undefined,
        address: address || undefined,
        city: city || undefined,
        zipcode: zipcode || undefined,
        latitude: latitude ? parseFloat(latitude) : undefined,
        longitude: longitude ? parseFloat(longitude) : undefined,
      });
      setCompetition(updatedCompetition);
      toast({
        title: t('competitionSettings.infoSaved'),
        description: t('competitionSettings.infoSavedDescription'),
      });
    } catch (error) {
      console.error('Failed to save competition info:', error);
      toast({
        title: t('competitionSettings.saveInfoError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setIsSavingInfo(false);
    }
  };

  const handleRemoveStaff = async (staff: Staff) => {
    if (!competition) return;

    const confirmed = window.confirm(
      t('competitionSettings.confirmRemoveDescription', { name: staff.user.full_name })
    );

    if (!confirmed) return;

    try {
      await staffService.removeStaff(competition.id, staff.user.username);
      setStaffList(prev => prev.filter(s => s.id !== staff.id));
      toast({
        title: t('competitionSettings.staffRemoved'),
        description: t('competitionSettings.staffRemovedDescription', { name: staff.user.full_name }),
      });
    } catch (error) {
      console.error('Failed to remove staff:', error);
      toast({
        title: t('competitionSettings.removeStaffError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    }
  };

  const handleAddStaff = async () => {
    if (!competition || !selectedUser || selectedRoles.length === 0) return;

    setIsCreating(true);
    try {
      const newStaff = await staffService.createStaff(competition.id, {
        user_slug: selectedUser.slug,
        staff_roles: selectedRoles
      });

      setStaffList(prev => [...prev, newStaff]);
      setShowAddForm(false);
      setSelectedUser(null);
      setSelectedRoles([]);
      setSearchQuery('');
      setSearchResults([]);

      toast({
        title: t('competitionSettings.staffInvited'),
        description: t('competitionSettings.staffInvitedDescription', { name: selectedUser.full_name }),
      });
    } catch (error) {
      console.error('Failed to add staff:', error);
      toast({
        title: t('competitionSettings.addStaffError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const toggleRole = (role: string) => {
    setSelectedRoles(prev =>
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  useBreadcrumb(
    () => ({
      onBack: () => navigate(-1),
      segments: [
        { label: competition?.name ?? '', onClick: () => navigate(`/competitions/${slug}`) },
        { label: t('competitionSettings.title') },
      ],
      loading,
    }),
    [competition?.name, slug, loading, t]
  );

  if (!slug) {
    return <Navigate to="/not-found" replace />;
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-48 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!competition || !currentUserStaff) {
    return <Navigate to="/not-found" replace />;
  }

  const isAdmin = currentUserStaff.staff_roles.includes('admin') && currentUserStaff.status === 'confirmed';

  if (!isAdmin) {
    return <Navigate to={`/competitions/${slug}`} replace />;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Competition Info */}
      <Card>
        <CardHeader>
          <CardTitle>{t('competitionSettings.infoSection')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Website */}
          <div className="space-y-2">
            <Label htmlFor="websiteUrl">{t('competitionSettings.websiteUrl')}</Label>
            <Input
              id="websiteUrl"
              type="url"
              placeholder={t('competitionSettings.websiteUrlPlaceholder')}
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
            />
          </div>

          {/* Address row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="address">{t('competitionSettings.address')}</Label>
              <Input
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">{t('competitionSettings.city')}</Label>
              <Input
                id="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
          </div>

          {/* Zipcode + Coordinates */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="zipcode">{t('competitionSettings.zipcode')}</Label>
              <Input
                id="zipcode"
                value={zipcode}
                onChange={(e) => setZipcode(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="latitude">{t('competitionSettings.latitude')}</Label>
              <Input
                id="latitude"
                type="number"
                step="any"
                min="-90"
                max="90"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="longitude">{t('competitionSettings.longitude')}</Label>
              <Input
                id="longitude"
                type="number"
                step="any"
                min="-180"
                max="180"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
              />
            </div>
          </div>

<Button onClick={handleSaveInfo} disabled={isSavingInfo}>
            {isSavingInfo ? t('competitionSettings.saving') : t('competitionSettings.saveInfo')}
          </Button>
        </CardContent>
      </Card>

      {/* Zone Management */}
      {slug && (
        <Card>
          <CardContent className="pt-6">
            <ZoneManager locationSlug={slug} isStaffMember={true} />
          </CardContent>
        </Card>
      )}

      {/* Staff Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            {t('competitionSettings.staffManagement')}
            <Button
              onClick={() => setShowAddForm(!showAddForm)}
              size="sm"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              {t('competitionSettings.addStaff')}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add Staff Form */}
          {showAddForm && (
            <Card className="border-dashed">
              <CardContent className="pt-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="userSearch">{t('competitionSettings.searchUser')}</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="userSearch"
                      placeholder={t('competitionSettings.searchUserPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="border rounded-md max-h-40 overflow-y-auto">
                      {searchResults.map((searchUser) => (
                        <div
                          key={searchUser.id}
                          className="p-3 hover:bg-muted cursor-pointer border-b last:border-b-0"
                          onClick={() => {
                            setSelectedUser(searchUser);
                            setSearchQuery(searchUser.full_name);
                            setSearchResults([]);
                          }}
                        >
                          <div className="font-medium">{searchUser.full_name}</div>
                          <div className="text-sm text-muted-foreground">@{searchUser.username}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected User */}
                {selectedUser && (
                  <div className="p-3 bg-muted rounded-md">
                    <div className="font-medium">{selectedUser.full_name}</div>
                    <div className="text-sm text-muted-foreground">@{selectedUser.username}</div>
                  </div>
                )}

                {/* Role Selection */}
                <div className="space-y-2">
                  <Label>{t('competitionSettings.selectRoles')}</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={selectedRoles.includes('admin') ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => toggleRole('admin')}
                    >
                      {t('competitionSettings.adminRole')}
                    </Button>
                    <Button
                      type="button"
                      variant={selectedRoles.includes('editor') ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => toggleRole('editor')}
                    >
                      {t('competitionSettings.editorRole')}
                    </Button>
                    <Button
                      type="button"
                      variant={selectedRoles.includes('judge') ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => toggleRole('judge')}
                    >
                      {t('competitionSettings.judgeRole')}
                    </Button>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    onClick={handleAddStaff}
                    disabled={!selectedUser || selectedRoles.length === 0 || isCreating}
                  >
                    {isCreating ? t('competitionSettings.inviting') : t('competitionSettings.sendInvitation')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowAddForm(false);
                      setSelectedUser(null);
                      setSelectedRoles([]);
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Staff List */}
          <div className="space-y-3">
            {staffList.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {t('competitionSettings.noStaff')}
              </div>
            ) : (
              staffList.map((staff) => (
                <div
                  key={staff.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex-1">
                    <div className="font-medium">{staff.user.full_name}</div>
                    <div className="text-sm text-muted-foreground">@{staff.user.username}</div>
                    <div className="flex gap-2 mt-2">
                      {staff.staff_roles.map((role) => (
                        <Badge key={role} variant="secondary">
                          {t(`competitionSettings.${role}Role`)}
                        </Badge>
                      ))}
                      <Badge
                        className={
                          staff.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                          staff.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }
                        variant={
                          staff.status === 'confirmed' ? 'default' :
                          staff.status === 'pending' ? 'secondary' : 'destructive'
                        }
                      >
                        {t(`competitionSettings.status.${staff.status}`)}
                      </Badge>
                    </div>
                  </div>

                  {/* Remove button - don't allow removing self */}
                  {staff.user.slug !== user?.slug && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      onClick={() => handleRemoveStaff(staff)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
